/**
 * Блок «Последние новости» на главной: свежая новость карточкой с обложкой,
 * ещё две — списком справа, и плашка «Не пропусти новое» со ссылкой на все
 * новости. Данные — GET /api/news (тот же API, что у страницы /news).
 * Пока новостей нет или раздел выключен — блок не рендерится.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import eventsImg from '../../images/advantages/3.png';
import { prefetchJson, fetchJsonCached, peekJson } from '../../utils/cachedJson';
import { trackPreload } from '../../utils/preloader';
import { rvDelay } from '../../utils/sectionReveal';
import styles from './latestnews.module.css';

const BASE = process.env.REACT_APP_ADMIN_API_URL ?? 'http://localhost:3001';

interface TNewsItem {
  slug: string;
  title: string;
  excerpt: string | null;
  cover_url: string | null;
  published_at: string | null;
}

function fmtDate(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function coverSrc(url: string | null): string | null {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BASE}${url}`;
}

/** Загрузить последние новости заранее (пока человек на другой странице сайта). */
export const prefetchLatestNews = () => prefetchJson(`${BASE}/api/news?page=1&limit=3`);

const LatestNews: React.FC = () => {
  const { newsSection } = useSiteConfig();
  const url = `${BASE}/api/news?page=1&limit=3`;
  // При возврате на главную новости берутся из кэша — экран не «доезжает»
  const [items, setItems] = React.useState<TNewsItem[]>(() => peekJson<{ items: TNewsItem[] }>(url)?.items ?? []);

  React.useEffect(() => {
    if (newsSection !== 'on') return undefined;
    let cancelled = false;
    trackPreload(fetchJsonCached<{ items: TNewsItem[] }>(url))
      .then(d => { if (!cancelled && d?.items) setItems(d.items); })
      .catch(() => { /* блок просто не показываем */ });
    return () => { cancelled = true; };
  }, [newsSection, url]);

  if (newsSection !== 'on' || items.length === 0) return null;
  const [first, ...rest] = items;
  const cover = coverSrc(first.cover_url);

  return (
    <section className={styles.section} id="latestnews">
      <h2 className={`${styles.title} rv`} style={rvDelay(0.05)}>Последние новости</h2>
      <div className={`${styles.line} rv rv-line`} style={rvDelay(0.2)} />
      <p className={`${styles.lead} rv`} style={rvDelay(0.28)}>Обновления сервера, ивенты и изменения в правилах</p>

      <div className={styles.grid}>
        <Link className={styles.card} to={`/news/${first.slug}`} onMouseEnter={() => prefetchJson(`${BASE}/api/news/${encodeURIComponent(first.slug)}`)}>
          {cover && <img className={styles.cover} src={cover} alt="" loading="lazy" />}
          <div className={styles.body}>
            <span className={styles.date}>{fmtDate(first.published_at)}</span>
            <h3 className={styles.cardTitle}>{first.title}</h3>
            {first.excerpt && <p className={styles.excerpt}>{first.excerpt}</p>}
          </div>
        </Link>

        <div className={styles.side}>
          {rest.map(n => (
            <Link key={n.slug} className={styles.card} to={`/news/${n.slug}`} onMouseEnter={() => prefetchJson(`${BASE}/api/news/${encodeURIComponent(n.slug)}`)}>
              <div className={styles.body}>
                <span className={styles.date}>{fmtDate(n.published_at)}</span>
                <h3 className={`${styles.cardTitle} ${styles.cardTitleSmall}`}>{n.title}</h3>
              </div>
            </Link>
          ))}
          <div className={styles.more}>
            <span className={styles.moreImg} style={{ backgroundImage: `url(${eventsImg})` }} aria-hidden="true" />
            <b className={styles.moreTitle}>Не пропусти новое</b>
            <p className={styles.moreText}>Анонсы ивентов, вайпов и обновлений лаунчера первыми появляются в Discord и VK.</p>
            <Link className={styles.moreBtn} to="/news">Все новости</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LatestNews;
